import { useMemo, useState } from "react";

import {
  filterKnownNumbers,
  flattenKnownNumberEntries,
  groupKnownNumbers,
} from "./knownNumbers";
import { useUiLanguage } from "./uiLanguageContext";
import type { KnownNumberEntry } from "./types";

type KnownNumberPickerProps = {
  entries: KnownNumberEntry[];
  maximumNumbers: number;
  onClear: () => void;
  onLimitReached: () => void;
  onToggle: (entry: KnownNumberEntry) => void;
  selectedEntries: KnownNumberEntry[];
};

export function KnownNumberPicker({
  entries,
  maximumNumbers,
  onClear,
  onLimitReached,
  onToggle,
  selectedEntries,
}: KnownNumberPickerProps) {
  const { text: t } = useUiLanguage();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const filtered = useMemo(
    () => filterKnownNumbers(entries, query),
    [entries, query],
  );
  const groups = useMemo(() => groupKnownNumbers(filtered), [filtered]);

  const selectedIds = useMemo(
    () => new Set(selectedEntries.map((entry) => entry.id)),
    [selectedEntries],
  );
  const selectedNumberCount = flattenKnownNumberEntries(selectedEntries).length;
  const selectionSummary = t(
    selectedNumberCount === 1 ? "selectedOne" : "selectedMany",
    { selected: selectedEntries.length, count: selectedNumberCount },
  );

  function choose(entry: KnownNumberEntry) {
    const selected = selectedIds.has(entry.id);
    if (!selected && selectedNumberCount + entry.numbers.length > maximumNumbers) {
      onLimitReached();
      return;
    }
    setQuery("");
    setOpen(false);
    setActiveIndex(0);
    onToggle(entry);
  }

  function clearSelection() {
    setQuery("");
    setOpen(false);
    setActiveIndex(0);
    onClear();
  }

  function moveActive(offset: number) {
    if (!open) {
      setOpen(true);
    }
    if (filtered.length === 0) {
      setActiveIndex(0);
      return;
    }
    setActiveIndex((current) =>
      Math.min(Math.max(current + offset, 0), filtered.length - 1),
    );
  }

  const activeEntry = filtered[activeIndex];

  return (
    <div
      className="known-number-picker"
      data-testid="known-number-picker"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setOpen(false);
        }
      }}
    >
      <label className="known-number-label" htmlFor="known-number-search">
        {t("knownAndSaved")}
      </label>
      <div
        className={`known-number-control${selectedEntries.length > 0 ? " known-number-control-selected" : ""}`}
      >
        <input
          id="known-number-search"
          type="text"
          role="combobox"
          aria-label={t("knownOrSavedSet")}
          aria-autocomplete="list"
          aria-controls="known-number-listbox"
          aria-expanded={open}
          aria-activedescendant={open && activeEntry ? `known-number-${activeEntry.id}` : undefined}
          autoComplete="off"
          placeholder={selectedEntries.length > 0
            ? selectionSummary
            : t("knownAndSaved")}
          size={selectedEntries.length > 0 ? selectionSummary.length : undefined}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActiveIndex(0);
            setOpen(true);
          }}
          onClick={() => setOpen(true)}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              moveActive(1);
            } else if (event.key === "ArrowUp") {
              event.preventDefault();
              moveActive(-1);
            } else if (event.key === "Enter" && open && activeEntry) {
              event.preventDefault();
              choose(activeEntry);
            } else if (event.key === "Escape") {
              setOpen(false);
            }
          }}
        />
          {selectedEntries.length > 0 ? (
            <button
              className="known-number-clear"
              type="button"
              aria-label={t("clearSelectedKnown")}
              title={t("clearSelections")}
              onMouseDown={(event) => event.preventDefault()}
              onClick={clearSelection}
            >
              <svg aria-hidden="true" viewBox="0 0 24 24">
                <path d="M7 7l10 10M17 7L7 17" />
              </svg>
            </button>
          ) : (
            <span className="known-number-chevron" aria-hidden="true">v</span>
          )}
      </div>
      {open ? (
        <div
          id="known-number-listbox"
          className="known-number-menu"
          role="listbox"
          aria-label={t("knownAndSavedSets")}
          aria-multiselectable="true"
        >
          {groups.length === 0 ? (
            <div className="known-number-empty">{t("noMatchingKnown")}</div>
          ) : (
            groups.map((group) => (
              <div
                className="known-number-group"
                role="group"
                aria-label={group.name}
                key={group.name}
              >
                <div className="known-number-group-title">{group.name}</div>
                {group.subgroups.map((subgroup) => (
                  <div className="known-number-subgroup" key={subgroup.name}>
                    <div className="known-number-subgroup-title">{subgroup.name}</div>
                    {subgroup.entries.map((entry) => {
                      const index = filtered.indexOf(entry);
                      const selected = selectedIds.has(entry.id);
                      const selectedEntry = selectedEntries.find(
                        (candidate) => candidate.id === entry.id,
                      );
                      const overLimit = !selected &&
                        selectedNumberCount + entry.numbers.length > maximumNumbers;
                      return (
                        <button
                          id={`known-number-${entry.id}`}
                          className={`known-number-option${index === activeIndex ? " known-number-option-active" : ""}${selected ? " known-number-option-selected" : ""}${overLimit ? " known-number-option-disabled" : ""}`}
                          type="button"
                          role="option"
                          aria-disabled={overLimit}
                          aria-selected={selected}
                          key={entry.id}
                          onMouseDown={(event) => event.preventDefault()}
                          onMouseEnter={() => setActiveIndex(index)}
                          onClick={() => choose(entry)}
                        >
                          <span className="known-number-option-label">
                            <span className="known-number-check" aria-hidden="true">
                              {selected ? "x" : ""}
                            </span>
                            <span>{entry.label}</span>
                          </span>
                          <span className="known-number-count">
                            {selectedEntry && selectedEntry.numbers.length !== entry.numbers.length
                              ? t("selectedOfMany", {
                                  selected: selectedEntry.numbers.length,
                                  total: entry.numbers.length,
                                })
                              : t(
                                  entry.numbers.length === 1
                                    ? "numberCountOne"
                                    : "numberCountMany",
                                  { count: entry.numbers.length },
                                )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
